import React from 'react';

import { Button, LoadingButton, Modal } from '@common/index';
import { capitalize, Stack } from '@mui/material';
import { useTheme } from '../../styles/theme';

import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { flowActions, tabActions } from '@store/slices';
import { AppDispatch, State } from '@store/index';
import { Report } from '@mui/icons-material';
import { deleteWorkflow } from '@requests/flow';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const DeleteTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const returnPath = useSelector((state: State) => state.flow.returnPath);
  const theme = useTheme();
  const queryClient = useQueryClient();

  const flows = useSelector((state: State) => state.flow.flows);
  const deleteFlowId = useSelector((state: State) => state.flow.deleteTab);
  const setOpen = (val: string | null) => dispatch(flowActions.setDeleteTab({ id: val }));

  const flowToDelete = flows.find((flow) => flow.flow_id === deleteFlowId);
  const type = returnPath === 'modules' ? 'module' : 'workflow';

  const { enqueueSnackbar } = useSnackbar();
  const { service_id } = useParams();

  const { mutateAsync: deleteByFlowId, isPending } = useMutation({
    mutationFn: deleteWorkflow,
    onSuccess: (data) => {
      if (!data) return;

      if (data?.error) {
        enqueueSnackbar('Failed to delete tab!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar(data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      setOpen(null);
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      navigate(`/${service_id}/${returnPath || 'workflow'}`);
      dispatch(tabActions.removeTab(data.workflow_id || ''));
      enqueueSnackbar(`${capitalize(type)} deleted successfully!`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    },
  });

  const discard = () => {
    if (!deleteFlowId) return;

    enqueueSnackbar('Deleting flow...', {
      variant: 'info',
      autoHideDuration: 3000,
    });

    deleteByFlowId({ workflow_id: deleteFlowId, service_id: service_id as string });
  };

  return (
    <Modal
      open={Boolean(deleteFlowId)}
      setOpen={setOpen as (val: boolean | string | null) => void}
      header={`Delete ${capitalize(type)}`}
      identifier={flowToDelete?.name}
      background={theme.telnyx.red}
      icon={<Report />}
      info={`Deleting this ${type} will delete all nodes, edges and any configurations associated with them from the database. This action cannot be undone. Are you sure you want to delete this ${type}?`}
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={() => setOpen(null)}>
            Back
          </Button>
          <LoadingButton minWidth='68px' loading={isPending} small customColor='red' onClick={discard}>
            Delete
          </LoadingButton>
        </Stack>
      }
    />
  );
};
